import React, { useState, useMemo } from "react";
import { ChevronDown, ChevronUp, Users, Target, TrendingUp } from "lucide-react";
import { CashDisplay, RunwayIndicator } from "../atoms";
import type { TimelineSnapshot } from "../../types/burndown.types";
import {
  calculateMonthlyCustomerMetrics,
  type CustomerProjectionData,
} from "../../utils/chart/customer-projection.utils";
import { calculateBreakEvenPlan } from "../../utils/chart/break-even.utils";

interface MobileFinancialSummaryProps {
  currentSnapshot: TimelineSnapshot | null;
  customerProjectionData?: CustomerProjectionData;
  currentDate?: Date;
}

export const MobileFinancialSummary: React.FC<MobileFinancialSummaryProps> = ({
  currentSnapshot,
  customerProjectionData,
  currentDate,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const roundedCashBalance = currentSnapshot
    ? Math.round(currentSnapshot.cashBalance * 100) / 100
    : 0;

  const customerMetrics =
    customerProjectionData && currentDate
      ? calculateMonthlyCustomerMetrics(currentDate, customerProjectionData)
      : null;

  const breakEvenPlan = useMemo(
    () => calculateBreakEvenPlan(currentSnapshot),
    [currentSnapshot],
  );

  return (
    <div className="lg:hidden border-b border-white/[0.08] bg-surface">
      <div className="relative px-4 py-4">
        <div className="flex items-center justify-center">
          <div className="text-center">
            <CashDisplay cashBalance={roundedCashBalance} />
            <div className="mt-2">
              <RunwayIndicator runway={currentSnapshot?.runway || Infinity} />
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className="absolute right-4 top-1/2 -translate-y-1/2 rounded-lg p-2 text-white/45 hover:bg-surface/10"
          aria-label={isExpanded ? "Collapse details" : "Expand details"}
        >
          {isExpanded ? (
            <ChevronUp className="h-5 w-5" />
          ) : (
            <ChevronDown className="h-5 w-5" />
          )}
        </button>
      </div>

      {isExpanded && currentSnapshot ? (
        <div className="border-t border-white/10 px-4 pb-4">
          <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
            <MetricTile label="Date" value={currentSnapshot.date.toLocaleDateString()} />
            <MetricTile
              label="Monthly burn"
              value={`$${Math.round(currentSnapshot.monthlyBurn).toLocaleString()}`}
              tone="danger"
            />
            <MetricTile
              label="Active expenses"
              value={String(currentSnapshot.activeExpenses.length)}
            />
            <MetricTile
              label="Monthly revenue"
              value={`$${Math.round(currentSnapshot.monthlyRevenue || 0).toLocaleString()}`}
              tone="brand"
            />
            {customerMetrics && customerMetrics.totalCustomers > 0 ? (
              <>
                <MetricTile
                  label="Customers"
                  value={customerMetrics.totalCustomers.toLocaleString()}
                  icon={<Users className="h-3.5 w-3.5" />}
                />
                <MetricTile
                  label="Net growth"
                  value={`${customerMetrics.netCustomerChange >= 0 ? "+" : ""}${Math.round(customerMetrics.netCustomerChange)}`}
                  tone={customerMetrics.netCustomerChange >= 0 ? "brand" : "danger"}
                />
              </>
            ) : null}
          </div>

          {breakEvenPlan ? (
            <div className="mt-3 space-y-2 border-t border-white/10 pt-3">
              <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-white/45">
                <Target className="h-3.5 w-3.5" />
                Break-even & growth
              </div>
              <MetricTile
                label="Operating break-even"
                value={`$${Math.round(breakEvenPlan.operatingBreakEvenRevenue).toLocaleString()}/mo`}
                tone="brand"
              />
              {breakEvenPlan.requiredMonthlyRevenueRamp ? (
                <MetricTile
                  label="Revenue ramp needed"
                  value={`+$${Math.round(breakEvenPlan.requiredMonthlyRevenueRamp).toLocaleString()}/mo`}
                  tone="warn"
                  icon={<TrendingUp className="h-3.5 w-3.5" />}
                />
              ) : (
                <MetricTile
                  label="Revenue gap"
                  value={
                    breakEvenPlan.isOperatingBreakEven
                      ? "At break-even"
                      : `+$${Math.round(breakEvenPlan.monthlyRevenueGap).toLocaleString()}/mo`
                  }
                  tone={breakEvenPlan.isOperatingBreakEven ? "brand" : "danger"}
                />
              )}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

function MetricTile({
  label,
  value,
  tone = "neutral",
  icon,
}: {
  label: string;
  value: string;
  tone?: "neutral" | "brand" | "danger" | "warn";
  icon?: React.ReactNode;
}) {
  const toneClasses = {
    neutral: "bg-surface/[0.04] text-white/90",
    brand: "bg-brand/10 text-violet-100",
    danger: "bg-expense/10 text-expense",
    warn: "bg-warn/10 text-amber-100",
  }[tone];

  return (
    <div className={`rounded-[14px] p-3 ${toneClasses}`}>
      <div className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-white/45">
        {icon}
        {label}
      </div>
      <div className="mt-1 font-bold">{value}</div>
    </div>
  );
}
